import { useEffect, useCallback, useState, useRef } from 'react';
import { trackButtonClick } from '../services/telemetry';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | React.ReactNode;
  variant?: 'alert' | 'confirm';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  confirmVariant?: 'primary' | 'danger' | 'primaryDark';
}

export const Dialog = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'alert',
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  confirmVariant = 'primary',
}: DialogProps) => {
  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackButtonClick('Dialog', 'Escape Key Dismiss', {
          dialogTitle: title,
        });
        onClose();
      }
    },
    [onClose, title]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    trackButtonClick('Dialog', confirmText, { dialogTitle: title });
    onConfirm?.();
    // Note: onConfirm handler is responsible for closing the dialog
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => {
          trackButtonClick('Dialog', 'Backdrop Dismiss', {
            dialogTitle: title,
          });
          onClose();
        }}
        aria-label="Close dialog"
      />

      {/* Dialog */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={`
          relative w-full max-w-md animate-[fadeIn_0.15s_ease-out] rounded-xl
          bg-white p-6 shadow-2xl
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2
          id="dialog-title"
          className="mb-4 text-xl font-semibold text-gray-900"
        >
          {title}
        </h2>

        {/* Message */}
        <div className="mb-6 whitespace-pre-line text-gray-600">{message}</div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          {variant === 'confirm' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                trackButtonClick('Dialog', cancelText, {
                  dialogTitle: title,
                });
                onClose();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={`
                cursor-pointer rounded-full border border-gray-800
                bg-transparent px-5 py-2 font-medium text-gray-800
                transition-all duration-100
                hover:bg-gray-800/5
                active:scale-95
              `}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (variant === 'confirm') {
                handleConfirm();
              } else {
                trackButtonClick('Dialog', confirmText, {
                  dialogTitle: title,
                });
                onClose();
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className={`
              cursor-pointer rounded-full px-5 py-2 font-medium transition-all
              duration-100
              active:scale-95
              ${
                confirmVariant === 'danger'
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }
            `}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook for easier dialog management
interface UseDialogOptions {
  title: string;
  message: string | React.ReactNode;
  variant?: 'alert' | 'confirm';
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger' | 'primaryDark';
}

export const useDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<UseDialogOptions>({
    title: '',
    message: '',
  });
  // Use ref instead of state for resolve function to avoid async state update issues
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const showAlert = (
    title: string,
    message: string | React.ReactNode
  ): Promise<void> => {
    return new Promise((resolve) => {
      setDialogOptions({
        title,
        message,
        variant: 'alert',
      });
      resolveRef.current = () => resolve();
      setIsOpen(true);
    });
  };

  const showConfirm = (
    title: string,
    message: string | React.ReactNode,
    options?: {
      confirmText?: string;
      cancelText?: string;
      confirmVariant?: 'primary' | 'danger' | 'primaryDark';
    }
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogOptions({
        title,
        message,
        variant: 'confirm',
        confirmText: options?.confirmText ?? 'Confirm',
        cancelText: options?.cancelText ?? 'Cancel',
        confirmVariant: options?.confirmVariant ?? 'primary',
      });
      resolveRef.current = resolve;
      setIsOpen(true);
    });
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const DialogComponent = () => (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogOptions.title}
      message={dialogOptions.message}
      variant={dialogOptions.variant}
      confirmText={dialogOptions.confirmText}
      cancelText={dialogOptions.cancelText}
      onConfirm={handleConfirm}
      confirmVariant={dialogOptions.confirmVariant}
    />
  );

  return {
    showAlert,
    showConfirm,
    DialogComponent,
  };
};
